import React, { useState, useContext, useRef, useEffect } from 'react';
import cn from 'classnames';
import IMask from 'imask';
import PropTypes from 'prop-types';
import { experimental } from '@trp-beacon-ui-kit/react-theme/warnings';
import actionTriangle from '@trp-beacon-ui-kit/react-theme/images/red-action-icon.svg';
import greenCheck from '@trp-beacon-ui-kit/react-theme/images/green-checkmark.svg';
import { FormContext } from '../Form';
import Formatters from './formatters';

import style from './styles.css';

const NOOP = () => ({});
const runFns = (...fns) => (event) => fns.map((fn = NOOP) => fn(event));

const InputFieldLabel = ({ title }) => title && <div className="field-title">{title}</div>;

// ie11 compat event creation
const createEvent = (type) => {
  if (typeof Event === 'function') return new Event(type);
  const ev = document.createEvent('Event');
  ev.initEvent(type, true, true);
  return ev;
};

const InputField = ({
  elementType,
  imgSource,
  name,
  required,
  title,
  titlePosition,
  validation,
  children,
  getValue,
  className,
  id,
  maskOptions,
  onChange,
  onBlur,
  formatter,
  transform,
  ...rest
}) => {
  const transformFn = Formatters[formatter] || transform;
  const onKeyUpTransform = (evt) => {
    evt.preventDefault();
    const { target } = evt;
    target.value = transformFn(target.value);
    const event = createEvent('change');
    target.dispatchEvent(event);
  };

  const formContext = useContext(FormContext);
  const inputEl = useRef(null);
  const themes = {
    success: {
      className: 'success',
      img: greenCheck,
      altText: 'Success Icon',
    },
    error: {
      className: 'error',
      img: actionTriangle,
      altText: 'Error Icon',
    },
    disabled: {
      className: 'disabled',
    },
    default: {
      className: '',
      img: imgSource || null,
      altText: 'Field Icon',
    },
  };

  const [touched, toggleTouch] = useState(false);
  const [inputValue, setInputValue] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [theme, setTheme] = useState(themes.default);

  const { values } = formContext || {};

  const validate = (val) => {
    setInputValue(val);
    if (!required && !val) {
      setTheme(themes.default);
      setErrorMessage(null);
      return;
    }

    if (validation && val) {
      const result = (Array.isArray(validation) ? validation.map((fn) => fn(val, values)) : [validation(val, values)])
        .filter((res) => typeof res === 'string')
        .join('\r\n');

      setTheme(themes[result ? 'error' : 'success']);
      setErrorMessage(result);
      return;
    }

    if (required) {
      setTheme(themes[val ? 'success' : 'error']);
      setErrorMessage(val ? null : 'Required');
    }
  };

  const validateRef = () => {
    const { current } = inputEl;
    if (!current) return;

    const val = getValue(current);
    validate(val);
  };

  // we want to set the error message state
  // when component renders.. but only once
  useEffect(() => {
    validateRef();
  }, []);

  const touchAndValidate = () => {
    toggleTouch(true);
    validateRef();
  };

  // if component exists in a form context
  // register submitHook to touch and validate the input
  // also provide the inputs value to the form context
  useEffect(() => {
    if (formContext) {
      const { registerInput, registerSubmitHook, unregisterSubmitHook, removeField } = formContext;
      const value = inputEl.current && getValue(inputEl.current);
      registerInput(name, errorMessage, transformFn ? transformFn(value) : value);
      registerSubmitHook(touchAndValidate);
      return () => {
        removeField(name);
        unregisterSubmitHook(touchAndValidate);
      };
    }
    return NOOP;
  }, [inputValue]);

  // If we have mask options
  // setup input masking using Imask library
  // destroy on unmount
  // see more docs here: https://imask.js.org/guide.html
  useEffect(() => {
    if (inputEl?.current) {
      if (transformFn) {
        inputEl.current.addEventListener('keyup', onKeyUpTransform);
        return () => inputEl.current.removeEventListener('keyup', onKeyUpTransform);
      }

      if (maskOptions) {
        const mask = IMask(inputEl.current, maskOptions);
        mask.on('accept', validateRef);
        return () => mask.destroy();
      }
    }
    return NOOP;
  }, [maskOptions, inputEl?.current]);

  const hasError = errorMessage && touched;

  // if not specified appends a "random" string
  // so multiple instances do not create invalid HTML
  const inputID =
    id ||
    `${name}-${Math.random()
      .toString(36)
      .slice(6)}`;

  return (
    <div className={cn('input-field', `title-layout-${titlePosition}`)}>
      <div className="input-container">
        <label htmlFor={inputID}>
          <InputFieldLabel title={title} />
          <div className="input-wrapper">
            {React.createElement(
              elementType,
              {
                className: cn(
                  {
                    [theme.className]: touched,
                  },
                  className,
                ),
                name,
                onBlur: runFns(onBlur, () => toggleTouch(true)),
                onChange: runFns(onChange, validateRef),
                ref: inputEl,
                id: inputID,
                ...rest,
              },
              ...React.Children.toArray(children),
            )}
            <div className="icon">
              {imgSource ? (
                <img className="input-icon" src={imgSource} alt={theme.altText} />
              ) : (
                touched && theme.img && <img className="input-icon" src={theme.img} alt={theme.altText} />
              )}
            </div>
          </div>
        </label>
      </div>
      {hasError && <div className="error-msg">{errorMessage}</div>}
      <style jsx>{style}</style>
    </div>
  );
};

InputField.defaultProps = {
  imgSource: null,
  required: false,
  title: '',
  validation: null,
  formatter: null,
  transform: null,
  maskOptions: null,
  elementType: 'input',
  className: '',
  getValue: ({ value }) => value,
  titlePosition: 'top',
};

InputField.propTypes = {
  /**
   * name of the input
   */
  name: PropTypes.string.isRequired,
  /**
   * validation function(s),
   * if an error message string is returned the
   * input fails validation otherwise it passes
   */
  validation: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.func)]),
  /**
   * a function to retrieve the value
   * from the underlying element
   * (checkbox e.g. (input) => input.checked ).
   * usually used in specific InputField implementations
   */
  getValue: PropTypes.func,
  /**
   * the actual underlying element type
   * (e.g. input, select, textarea)
   */
  elementType: PropTypes.elementType,
  imgSource: PropTypes.node,
  /* is a value required for the InputField */
  required: PropTypes.bool,
  /* A label to be used for the input */
  title: PropTypes.node,
  /* position for your title in relation to input */
  titlePosition: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  /**
   * flag used to add quantum replay
   * masking of input, by adding an
   * additonal className to the field
   */
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  /**
   * This function transforms the input on keyup
   * useful for formatting things like phone numbers
   * ssn or other values that contain dashes/commas etc
   */
  transform: PropTypes.func,
  /**
   * This prop specifies the transform from the list
   * of default transform functions (e.g. SSN, EIN, LOCALE_STRING, PHONE)
   */
  formatter: PropTypes.oneOf(['LOCALE_STRING', 'DECIMAL_CURRENCY', 'PERCENT', 'SSN', 'DATE', 'EIN', 'PHONE']),
  /**
   * This is a configuration object passed to iMask.js
   * to support input masking, this is useful for
   * formatting inputs, like phone numbers, emails, SSNs
   * so that dashes/symbols/delimiters are automatically inserted
   * and invalid characters removed. Find out more info here.
   * More docs on how to use iMask can be found at https://imask.js.org/)
   */
  maskOptions: PropTypes.object,
};

export default experimental(InputField);
